import dayjs from 'dayjs';
import { useCallback } from 'react';

import { ScheduleActions } from '@Actions/ScheduleActions';
import { showNotification, NotificationType } from '@Components/design-system/notification/notification';
import { IQueryData } from '@Reducers/common';
import { IRescheduleConsultationData } from '@Reducers/consultation';
import { IAvailableLanguages } from '@Reducers/schedule';
import { IUserData } from '@Reducers/user';
import { useAppDispatch, useAppSelector } from '@Redux/hooks';
import { CONSULTATION_TYPE, INSTANT_LOCATION_CODE } from '@Utils/constants';
import useAnasRouter from '@Utils/hooks/anas-router';
import { Routes } from '@Utils/hooks/anas-router/routes';
import usePosthog, { CtaEvents } from '@Utils/hooks/posthog';

import useConsultationType from '../../consultation-type';
import useGetDays from '../../get-days';
import { Direction } from '../constants';
import useOnEnter from './on-enter';
import { Rule } from './type';

export const useLanguageRule = (): Rule => {
  const dispatch = useAppDispatch();
  const router = useAnasRouter();
  const { consultationType, locationType } = useConsultationType();
  const consultation: IRescheduleConsultationData = useAppSelector(
    (state) => state.consultation.rescheduleConsultationData,
  );
  const { startDate, endDate } = useGetDays(consultationType, locationType, consultation?.dueDate);
  const queryData: IQueryData = useAppSelector((state) => state.common.queryData);
  const userData: IUserData = useAppSelector((state) => state.user.userData);
  const availableLanguages: IAvailableLanguages = useAppSelector((state) => state.schedule.availableLanguages);
  const { ctaEventCapture } = usePosthog();

  const next = useCallback(() => {
    ctaEventCapture(CtaEvents.LANGUAGE);
    if (!queryData?.selectedLanguages?.length) {
      showNotification(NotificationType.ERROR, 'Please select languages', 3000);
      return;
    }
    if (queryData.locationCode === INSTANT_LOCATION_CODE) {
      router.push({
        route: Routes.APPOINTMENT,
        subRoute: queryData?.skipQuestionnaire ? Direction.CHECKOUT : Direction.QUESTIONNAIRE,
      });
      return;
    }
    router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
  }, [queryData?.selectedLanguages?.length]);

  useOnEnter(() => {
    if (!consultationType?.consultationTypeId) return;
    if (
      (consultationType.consultationCode === CONSULTATION_TYPE.TH
        ? !!userData?.therapistId
        : !!userData?.physicianId) ||
      (queryData.providerId?.[0] && queryData.locationCode !== INSTANT_LOCATION_CODE)
    ) {
      router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
      return;
    }
    if (availableLanguages?.languages?.length !== 0) return;
    dispatch(
      ScheduleActions.GetAvailableLanguages({
        consultationTypeId: consultationType.consultationTypeId,
        startTime: dayjs(startDate).toISOString(),
        endTime: dayjs(endDate).endOf('day').toISOString(),
      }),
    );
  }, [consultationType]);

  return {
    next,
    disableNext: !queryData?.selectedLanguages?.length,
    isLoading: availableLanguages?.isLoading,
  };
};
